import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, User, Mail, Phone, MapPin, Car, Package, Trash2, Check, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/appointments`, {
        withCredentials: true
      });
      setAppointments(data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/appointments/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`Appointment marked as ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/appointments/${id}`, {
        withCredentials: true
      });
      toast.success('Appointment deleted');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    return apt.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading appointments...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Appointments</h1>
        <p className="text-gray-600">Manage your booking requests</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold">{appointments.length}</div>
            <div className="text-gray-600">Total</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-yellow-600">
              {appointments.filter(a => a.status === 'pending').length}
            </div>
            <div className="text-gray-600">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-blue-600">
              {appointments.filter(a => a.status === 'confirmed').length}
            </div>
            <div className="text-gray-600">Confirmed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold text-green-600">
              {appointments.filter(a => a.status === 'completed').length}
            </div>
            <div className="text-gray-600">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => setFilter('all')}
          variant={filter === 'all' ? 'default' : 'outline'}
          className={filter === 'all' ? 'cta-button' : ''}
        >
          All
        </Button>
        <Button
          onClick={() => setFilter('pending')}
          variant={filter === 'pending' ? 'default' : 'outline'}
          className={filter === 'pending' ? 'cta-button' : ''}
        >
          Pending
        </Button>
        <Button
          onClick={() => setFilter('confirmed')}
          variant={filter === 'confirmed' ? 'default' : 'outline'}
          className={filter === 'confirmed' ? 'cta-button' : ''}
        >
          Confirmed
        </Button>
        <Button
          onClick={() => setFilter('completed')}
          variant={filter === 'completed' ? 'default' : 'outline'}
          className={filter === 'completed' ? 'cta-button' : ''}
        >
          Completed
        </Button>
      </div>

      {/* Appointments List */}
      <div className="grid gap-6">
        {filteredAppointments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-xl text-gray-500">
                {filter === 'all' ? 'No appointments yet' : `No ${filter} appointments`}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAppointments.map((appointment) => (
            <Card key={appointment.id} className="border-0 shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{appointment.name}</h3>
                    <Badge className={getStatusColor(appointment.status)}>
                      {appointment.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(appointment.createdAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-lg mb-3">Contact Information</h4>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${appointment.phone}`} className="hover:underline">
                        {appointment.phone}
                      </a>
                    </div>
                    {appointment.email && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${appointment.email}`} className="hover:underline">
                          {appointment.email}
                        </a>
                      </div>
                    )}
                    <div className="flex items-start gap-2 text-gray-700">
                      <MapPin className="w-4 h-4 mt-1" />
                      <span>{appointment.location}</span>
                    </div>
                  </div>

                  {/* Service Details */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-lg mb-3">Service Details</h4>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Package className="w-4 h-4" />
                      <span>{appointment.service}</span>
                    </div>
                    {appointment.vehicleType && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Car className="w-4 h-4" />
                        <span>{appointment.vehicleType}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="w-4 h-4" />
                      <span>{appointment.preferredDate}</span>
                    </div>
                    {appointment.preferredTime && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="w-4 h-4" />
                        <span>{appointment.preferredTime}</span>
                      </div>
                    )}
                  </div>
                </div>

                {appointment.notes && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Notes:</h4>
                    <p className="text-gray-700">{appointment.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-6 pt-6 border-t">
                  {appointment.status === 'pending' && (
                    <Button
                      onClick={() => updateStatus(appointment.id, 'confirmed')}
                      className="cta-button"
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-1" /> Confirm
                    </Button>
                  )}
                  {appointment.status === 'confirmed' && (
                    <Button
                      onClick={() => updateStatus(appointment.id, 'completed')}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-1" /> Mark Complete
                    </Button>
                  )}
                  {appointment.status !== 'cancelled' && (
                    <Button
                      onClick={() => updateStatus(appointment.id, 'cancelled')}
                      variant="outline"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => deleteAppointment(appointment.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminAppointments;
